// noinspection JSUnusedGlobalSymbols

//region Imports
import type {NextPage} from "next";
import React, {useState, useEffect} from "react";
import {useRouter} from "next/router";
import dynamic from "next/dynamic";
import type {Application} from '@splinetool/runtime';
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from 'lodash';

import {pages} from "@isudio/config";
import {$store} from '@isudio/globals';
import {$core} from "@isudio/core";
import {useRxSync} from '@isudio/utils';
import {PageHead,  SEOContent, LoadingSpinner, VideoPlayer, useOpenComponent, useDialogController, FadeTransition, getSplinePreloadPromise} from '@isudio/ui';
import type {ISignupInvite, IUserSignupResponse, IUser} from "@isudio/data";

const SplineObject = dynamic(
  () => import('@isudio/ui').then((mod) => mod.SplineObject),
  { ssr: false }
);
//endregion Imports

const Home: NextPage = () => {
  //region Variables
  const router = useRouter();
  const [queryParams, setQueryParams] = useState<any>(null);
  const windowState: any = _.get(useRxSync($store.App.state$), 'window');
  const [loading, setLoading] = useState(true);
  // const [splineApp, setSplineApp] = useState<Application>(null);
  // const user: any = useRxSync($store.User.user$);
  // const [userAvailable, setUserAvailable] = React.useState(!!user);
  // const userSignedIn: boolean = useRxSync($store.User.signedIn$);

  const pageTitle = pages.home.title;
  const pageCaption = pages.home.caption;
  const pageImage = pages.home.image;
  const pageUrl = pages.home.url;

  const seoContent = pages.home.seoContent;
  const splineSettings = pages.home.spline.settings;
  const splineLinks: any[] = pages.home.spline.links;
  const [liveSplineUrl, setLiveSplineUrl] = useState<string>(splineSettings?.url);
  const [splineReady, setSplineReady] = useState(false);
  const splineLinkNames: string[] = splineLinks.map((o: any) => o.name);

  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  //endregion Variables

  //region Methods & Event Handlers
  const {isDialogOpen} = useDialogController();
  const openComponent = useOpenComponent();

  const openSignup = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {email: queryParams.signup}
      };

      await $core.verifyInvite(payload).then((data: ISignupInvite) => {
        const _data = {
          accountType: data.role,
          email: data.email,
          organization: data.organization,
          institution: data.institution
        }
        openComponent("signUp", _data);

      }).catch((e: any) => {
        const x = setTimeout(async () => {
          _.toastError(_.get(e, 'message'));
          clearTimeout(x);
        }, 10);
      });

      window.history.replaceState({}, '', window.location.pathname);
      clearTimeout(t);
    }, 100);
  };

  const openActivate = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {
          email: queryParams.activate,
          otp: queryParams.otp
        }
      };

      await $core.verifyActivation(payload).then((user: IUserSignupResponse) => {
        $store.User.user$.next(user);
        const _data = {
          user,
          otp: queryParams.otp
        };
        openComponent("activateAccount", _data);

      }).catch((e: any) => {
        if (['otpExpired', 'otpIncorrect'].indexOf(_.get(e, 'code')) > -1) {
          const _data = {
            user: e?.data,
            otp: queryParams.otp,
            error: e
          };
          openComponent("activateAccount", _data);

        } else {
          const x = setTimeout(async () => {
            _.toastError(_.get(e, 'message'));
            clearTimeout(x);
          }, 10);
        }
      });

      window.history.replaceState({}, '', window.location.pathname);
      clearTimeout(t);
    }, 100);
  };

  const openChangePassword = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {
          email: queryParams.changePassword,
          otp: queryParams.otp
        }
      };

      await $core.verifyChangePassword(payload).then((user: IUser) => {
        $store.User.user$.next(user);
        const _data = {
          user: user,
          otp: queryParams.otp
        };
        openComponent("changePassword", _data);

      }).catch((e: any) => {
        if (['otpExpired', 'otpIncorrect'].indexOf(_.get(e, 'code')) > -1) {
          const _data = {
            user: e?.data,
            otp: queryParams.otp,
            error: e
          };
          openComponent("changePassword", _data);

        } else {
          const x = setTimeout(async () => {
            _.toastError(_.get(e, 'message'));
            clearTimeout(x);
          }, 10);
        }
      });

      window.history.replaceState({}, '', window.location.pathname);
      clearTimeout(t);
    }, 100);
  };

  const onSplineScroll = async () => {
    $store.App.showMenu$.next(false);
  };

  const onSplineClick = async (e: any) => {
    if (!e?.target?.name) return;

    const name = (
      e.target.name?.startsWith('pSignup') ||
      e.target.name?.startsWith('pSendBrief') ||
      e.target.name?.startsWith('pSignin') ||
      e.target.name?.startsWith('pCareers') ||
      e.target.name?.startsWith('lSignup') ||
      e.target.name?.startsWith('lSendBrief') ||
      e.target.name?.startsWith('lSignin') ||
      e.target.name?.startsWith('lCareers')
    ) ? e.target.name.slice(1) : e.target.name;

    const target: any = _.find(splineLinks, {name: name});
    if (!_.isNothing(target)) {
      switch (target.type) {
        case "menu":
          $store.App.menuClicked$.next(true);
          break;

        case "video":
          setVideoUrl(target.url);
          break;

        case "dialog":
          openComponent(target.id)
          break;

        case "route":
          await router.push(target.url, undefined, {shallow: false});
          break;

        default:
          _.toastSuccess(`${target.name} clicked!`);
      }
    }
  };

  const onLoadComplete = (_app: Application) => {
    // setSplineApp(app);
    setLoading(false);
  };
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    if (!router.isReady) return;

    setQueryParams({
      signup: router.query.signup,
      activate: router.query.activate,
      changePassword: router.query.changePassword,
      otp: router.query.otp
    })
  }, [router.isReady, router.query.signup, router.query.activate, router.query.changePassword, router.query.otp]);

  // useEffect(() => {
  //   setUserAvailable(!!user);
  // }, [user]);

  useEffect(() => {
    let mounted = true;

    async function waitForSpline() {
      try {
        const preload = getSplinePreloadPromise();

        if (preload) {
          // 1. Await the promise to get the local Blob URL string
          const blobUrl = await preload;

          // 2. Save it to state if the component is still mounted
          if (mounted && blobUrl) {
            setLiveSplineUrl(blobUrl);
          }
        }
      } catch (err) {
        console.error(err);
      }

      if (mounted) {
        setSplineReady(true);
      }
    }

    waitForSpline().finally();

    return () => {
      mounted = false;
    };
  }, [splineSettings?.url]);

  useEffect(() => {
    if (!loading && queryParams) {
      if (queryParams.signup) {
        openSignup();
      }

      if (queryParams.activate) {
        openActivate();
      }

      if (queryParams.changePassword) {
        openChangePassword();
      }
    }
  }, [loading, queryParams]);

  // useEffect(() => {
  //   if (!windowState || !windowState?.orientation) return;
  //   if (!splineApp) return;
  //
  //   const prefix = _.first(windowState.orientation);
  //   const signup = splineApp.findObjectByName(`${prefix}Signup`);
  //   const sendBrief = splineApp.findObjectByName(`${prefix}SendBrief`);
  //   const sendBriefLink = splineApp.findObjectByName(`${prefix}SendBriefLink`);
  //   const signinLink = splineApp.findObjectByName(`${prefix}SigninLink`);
  //   const careersLink = splineApp.findObjectByName(`${prefix}CareersLink`);
  //   if (!signup || !sendBrief || !sendBriefLink || !signinLink || !careersLink) return;
  //
  //   if (userAvailable) {
  //     if (userSignedIn) {
  //       signup.visible = false;
  //       sendBrief.visible = true;
  //       sendBriefLink.visible = false;
  //       signinLink.visible = false;
  //       careersLink.visible = true;
  //
  //     } else {
  //       signup.visible = false;
  //       sendBrief.visible = true;
  //       sendBriefLink.visible = false;
  //       signinLink.visible = true;
  //       careersLink.visible = false;
  //     }
  //
  //   } else {
  //     signup.visible = true;
  //     sendBrief.visible = false;
  //     sendBriefLink.visible = true;
  //     signinLink.visible = false;
  //     careersLink.visible = false;
  //   }
  // }, [windowState?.orientation, splineApp, userAvailable, userSignedIn]);
  //endregion Effects

  //region HTML Elements
  const loadingSpinner = (
    <div
      className="fixed inset-0 z-9999 bg-bluegray-darker/85">
      <LoadingSpinner
       // image="/assets/media/loader/loader.gif"
       video="/assets/media/loader/loader.webm"
      />
    </div>
  );

  const seoElement = (
    <SEOContent
      pageTitle={pageTitle}
      seoContent={seoContent}
    />
  );

  const splineObject = (
    <SplineObject
      windowState={windowState}
      splineSettings={{ ...splineSettings, url: liveSplineUrl }}
      links={splineLinkNames}
      onLoadComplete={onLoadComplete}
      onSplineClick={onSplineClick}
      onSplineScroll={onSplineScroll}
    />
  );

  const toastContainer = (
    <ToastContainer
      position="bottom-center"
      autoClose={3000}
      newestOnTop={true}
      hideProgressBar={true}
      closeButton={false}
      closeOnClick={true}
      pauseOnHover={true}
      draggable={false}
      theme="dark"
      transition={FadeTransition}
      className="absolute left-0 right-0 bottom-0 z-9999 pointer-events-none"
      toastClassName="mx-auto pointer-events-auto flex !m-0 items-center gap-2 rounded-xl shadow-lg border-none transition-all duration-200"
    />
  );

  const videoPlayer = (
    <VideoPlayer
      url={videoUrl}
      onClose={() => setVideoUrl(null)}
    />
  );
  //endregion HTML Elements

  return (
    <>
      <PageHead
        title={pageTitle}
        caption={pageCaption}
        image={pageImage}
        url={pageUrl}
      />

      <div
        className="w-full h-full relative overflow-hidden">
        {loading && (
          loadingSpinner
        )}

        {seoElement}

        {splineReady && splineObject}

        {!isDialogOpen && (
          toastContainer
        )}

        {videoUrl && (
          videoPlayer
        )}
      </div>
    </>
  );
};

export default Home;